import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Send, ShieldAlert } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usePersonalDataGuard } from "../../hooks/usePersonalDataGuard";
import {
  useThreadMessages,
  useSendMessage,
  useMarkThreadRead,
} from "../../hooks/queries/useDirectMessages";
import ContactRequestContext from "./ContactRequestContext";

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatDateSeparator = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Hoy";
  if (d.toDateString() === yesterday.toDateString()) return "Ayer";
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const ChatThread = ({ contactRequestId, thread, onBack }) => {
  const { authUserId } = useAuth();
  const bottomRef = useRef(null);
  const [draft, setDraft] = useState("");
  const { piiWarning, checkMessage, clearWarning } = usePersonalDataGuard();

  const messagesQuery = useThreadMessages(contactRequestId);
  const sendMessage = useSendMessage();
  const { mutate: markRead } = useMarkThreadRead();

  const messages = messagesQuery.data || [];

  useEffect(() => {
    if (contactRequestId) {
      markRead({ contactRequestId });
    }
  }, [contactRequestId, markRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sendMessage.isPending) return;
    if (checkMessage(body).blocked) return;
    setDraft("");
    await sendMessage.mutateAsync({ contactRequestId, body });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const otherName = thread?.other_party?.full_name || "Conversación";
  const orgName = thread?.other_party?.organization_name;

  let lastDateKey = "";

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label="Volver"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-gray-900">{otherName}</h2>
          {orgName && (
            <p className="truncate text-xs text-gray-500">{orgName}</p>
          )}
        </div>
      </div>

      {/* Contact request context (collapsible) */}
      <ContactRequestContext request={thread?.request} />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messagesQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-blue-500" />
          </div>
        ) : messages.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-400">
            No hay mensajes aún. Escribe el primero.
          </p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_user_id === authUserId;
            const dateKey = new Date(msg.created_at).toDateString();
            let showDate = false;
            if (dateKey !== lastDateKey) {
              showDate = true;
              lastDateKey = dateKey;
            }

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="my-4 flex items-center gap-3">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-xs text-gray-400">
                      {formatDateSeparator(msg.created_at)}
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                )}
                <div
                  className={`mb-2 flex ${isOwn ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                      isOwn
                        ? "rounded-br-md bg-blue-600 text-white"
                        : "rounded-bl-md bg-gray-100 text-gray-900"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words text-sm">
                      {msg.body}
                    </p>
                    <div
                      className={`mt-1 flex items-center gap-1 ${
                        isOwn ? "justify-end" : "justify-start"
                      }`}
                    >
                      <span
                        className={`text-[10px] ${
                          isOwn ? "text-blue-200" : "text-gray-400"
                        }`}
                      >
                        {formatTime(msg.created_at)}
                      </span>
                      {isOwn && msg.read_at && (
                        <span className="text-[10px] text-blue-200">✓✓</span>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* PII warning */}
      {piiWarning && (
        <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <ShieldAlert size={16} className="shrink-0" />
          <span>{piiWarning}</span>
        </div>
      )}

      {/* Input — padded past the iOS home indicator (env() is 0 on the web). */}
      <div
        className="border-t border-gray-200 bg-white px-4 pt-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); if (piiWarning) clearWarning(); }}
            onKeyDown={handleKeyDown}
            placeholder="Escribe un mensaje..."
            rows={1}
            className="max-h-24 min-h-[40px] flex-1 resize-none rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sendMessage.isPending}
            className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Enviar"
          >
            {sendMessage.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatThread;
